// Global Instructions Rule Applied!
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from '../../../state_manager/store.ts'
import { fetchReportPreview } from '../services/documentReportsService.ts'
import type { ReportHighlightsStatus } from '../../../state_manager/data_models/period_highlights_model.ts'

interface UseDocumentReportPreviewResult {
  blobUrl: string | null
  pageCount: number | null
  /** Review status of the highlights the previewed report carries. */
  highlights: ReportHighlightsStatus | null
  /** Companies whose agent-written short description it prints unreviewed. */
  unreviewedDescriptions: string[]
  loading: boolean
  error: string | null
  /** Fetch (or refetch) the preview. Called on Apply, never per keystroke. */
  refresh: (reportCode: string, params: Record<string, unknown>) => Promise<void>
  clear: () => void
}

/**
 * Owns the preview document's lifecycle.
 *
 * Each preview is a fresh object URL, so the previous one is revoked on every
 * replacement and on unmount — otherwise each Apply would leak a document
 * (which, with the fonts inlined, is over half a megabyte a time).
 */
export function useDocumentReportPreview(): UseDocumentReportPreviewResult {
  const accessToken = useSelector((state: RootState) => state.auth?.accessToken)
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [pageCount, setPageCount] = useState<number | null>(null)
  const [highlights, setHighlights] = useState<ReportHighlightsStatus | null>(null)
  const [unreviewedDescriptions, setUnreviewedDescriptions] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Held in a ref as well as state so cleanup can revoke without re-running
  // the effect every time the URL changes.
  const currentUrl = useRef<string | null>(null)

  const replaceUrl = useCallback((next: string | null) => {
    if (currentUrl.current) URL.revokeObjectURL(currentUrl.current)
    currentUrl.current = next
    setBlobUrl(next)
  }, [])

  // A slow render must not be overwritten by an earlier one that lands later.
  const requestSeq = useRef(0)

  const refresh = useCallback(
    async (reportCode: string, params: Record<string, unknown>) => {
      const seq = ++requestSeq.current
      setLoading(true)
      setError(null)

      // The preview is the LIVE rendering: entity names carry in-app links
      // (relayed by the preview pane). The PDF path sends 'export' instead.
      const result = await fetchReportPreview(
        { reportCode, params: { ...params, link_mode: 'app' } },
        accessToken ?? null
      )

      if (seq !== requestSeq.current) {
        // Superseded — drop this response, including its object URL.
        if (result.blobUrl) URL.revokeObjectURL(result.blobUrl)
        return
      }

      if (result.success && result.blobUrl) {
        replaceUrl(result.blobUrl)
        setPageCount(result.pageCount ?? null)
        setHighlights(result.highlights ?? null)
        setUnreviewedDescriptions(result.unreviewedDescriptions ?? [])
      } else {
        replaceUrl(null)
        setPageCount(null)
        setHighlights(null)
        setUnreviewedDescriptions([])
        setError(result.error || 'Failed to render the report')
      }
      setLoading(false)
    },
    [accessToken, replaceUrl]
  )

  const clear = useCallback(() => {
    requestSeq.current += 1
    replaceUrl(null)
    setPageCount(null)
    setHighlights(null)
    setUnreviewedDescriptions([])
    setError(null)
    setLoading(false)
  }, [replaceUrl])

  useEffect(() => {
    return () => {
      if (currentUrl.current) URL.revokeObjectURL(currentUrl.current)
      currentUrl.current = null
    }
  }, [])

  return { blobUrl, pageCount, highlights, unreviewedDescriptions, loading, error, refresh, clear }
}
